import "core-js/modules/es6.array.filter";
import "core-js/modules/es6.array.map";
import _difference from "lodash/difference";
import _includes from "lodash/includes";
import _get from "lodash/get";
import { PROVIDER_STATUS_POLLING_INTERVAL, ProviderAccountState } from "../constants";
import { yodlee as yodleeUtils } from "../utils";
import { readYodleeProfile } from "../actions";
import { getAllProviderAccounts, getIdentityId } from "../selectors";
import { startPolling } from "../utils/polling";

var getProviderAccountId = function getProviderAccountId(account) {
  return _get(account, 'provider_account_state.provider_account_id');
};
/**
 * All states but ERROR and ACTIVE require polling as the state can change on
 * the server without user (client) action. ERROR and ACTIVE cannot change
 * unless the user (client) does something.
 */


var statesToPoll = [ProviderAccountState.LOGIN_IN_PROGRESS, ProviderAccountState.MFA_REQUIRED, ProviderAccountState.MFA_IN_PROGRESS, ProviderAccountState.AUTHENTICATED];
/**
 * Determine if the given provider account is in a state that requires
 * polling.
 *
 * Accounts need polling if they are in one of the statesToPoll, or the
 * account data is syncing (is_active_sync), or the account is refreshing,
 * or the account has transactions syncing.
 *
 * @param {Object} providerAccount Provider account state object
 * @returns {boolean} Whether the given provider account requires polling
 */

var doesAccountNeedPolling = function doesAccountNeedPolling(providerAccount) {
  var providerAccountState = _get(providerAccount, 'provider_account_state');

  var providerAccountStateState = _get(providerAccount, 'provider_account_state.state');

  var isAccountRefreshing = yodleeUtils.isProviderAccountRefreshing(providerAccount);
  return _includes(statesToPoll, providerAccountStateState) || providerAccountState.is_active_sync || isAccountRefreshing || yodleeUtils.areProviderAccountTransactionsSyncing(providerAccount);
};
/**
 * Stateful representation of accounts that need polling, and the polling
 * state itself (whether we are currently polling, number of times we've
 * polled).
 *
 * Returns a function that handles state changes, taking the new Redux store
 * state.
 *
 * @param {Object} obj
 * @param {Function} obj.dispatch Redux dispatch function, used for polling
 * @param {number} [obj.pollingInterval=4s] Base delay between polling
 * @param {Function} [obj.isOffline] Optional check to determine if the
 * network is offline, which will stop polling.
 * @return {Function} Handles state changes
 */


var createAccountPoller = function createAccountPoller() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      dispatch = _ref.dispatch,
      _ref$pollingInterval = _ref.pollingInterval,
      pollingInterval = _ref$pollingInterval === void 0 ? PROVIDER_STATUS_POLLING_INTERVAL : _ref$pollingInterval,
      _ref$isOffline = _ref.isOffline,
      isOffline = _ref$isOffline === void 0 ? function () {
    return false;
  } : _ref$isOffline;

  /**
   * Current polling state. If polling is active, this will be a function that
   * stops polling. Otherwise it will be null. This state is controlled by
   * ensureStartedPolling and ensureStoppedPolling.
   */
  var stopPolling = null;
  /**
   * Accounts that need polling
   */

  var accountsThatNeedPolling = [];
  /**
   * Function that checks for new provider account states  and updates states.
   */

  var poll = function poll() {
    return dispatch(readYodleeProfile());
  };
  /**
   * Function that determines how long to wait until the next poll.
   *
   * Poll every four seconds for roughly the first 30 seconds, then poll every
   * 16 seconds (based on the default pollingInterval of four seconds.
   */


  var getDelay = function getDelay(count) {
    return count < 8 ? pollingInterval : 4 * pollingInterval;
  };
  /**
   * Start polling (if not already started).
   */


  var ensureStartedPolling = function ensureStartedPolling() {
    if (stopPolling == null) {
      stopPolling = startPolling({
        poll: poll,
        getDelay: getDelay
      });
    }
  };
  /**
   * Stop polling (if not already stopped).
   */


  var ensureStoppedPolling = function ensureStoppedPolling() {
    if (stopPolling) {
      stopPolling();
      stopPolling = null;
    }
  };
  /**
   * Stop and restart polling, resetting the current delay.
   */


  var resetPolling = function resetPolling() {
    ensureStoppedPolling();
    ensureStartedPolling();
  };

  var handleState = function handleState(state) {
    if (isOffline(state)) {
      ensureStoppedPolling();
      return;
    }

    var currentIdentityId = getIdentityId(state.identity);
    var prevAccountsThatNeedPolling = accountsThatNeedPolling;
    accountsThatNeedPolling = getAllProviderAccounts(state.identity).filter(doesAccountNeedPolling).map(getProviderAccountId);
    /*
     * If there are no accounts left to poll, or if the user's identity
     * id is null or undefined i.e. they're logged out, stop polling.
     */

    if (accountsThatNeedPolling.length === 0 || !currentIdentityId) {
      ensureStoppedPolling();
    } else {
      // If a new account was added, reset the polling delay
      var newAccounts = _difference(accountsThatNeedPolling, prevAccountsThatNeedPolling);

      if (newAccounts.length > 0) {
        // Reset polling delay
        resetPolling();
      }

      ensureStartedPolling();
    }
  };

  return handleState;
};
/**
 * Creates a Redux middleware that polls for provider account state updates.
 *
 * @param {Object} [obj]
 * @param {number} [obj.pollingInterval] Base delay between polling
 * @param {Function} [obj.isOffline] Optional isOfflineCheck
 * @returns {Function} Redux middleware
 */


export default (function () {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      pollingInterval = _ref2.pollingInterval,
      isOffline = _ref2.isOffline;

  return (// Redux middleware
    function (_ref3) {
      var dispatch = _ref3.dispatch,
          getState = _ref3.getState;
      // No polling when running on server -- noop middleware
      if (!process.browser) return function (next) {
        return function (action) {
          return next(action);
        };
      };
      var handleState = createAccountPoller({
        dispatch: dispatch,
        pollingInterval: pollingInterval,
        isOffline: isOffline
      });
      handleState(getState());
      return function (next) {
        return function (action) {
          var returnValue = next(action);
          handleState(getState());
          return returnValue;
        };
      };
    }
  );
});